import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearOrders, fetchOrders, refundTickets, setOrdersFilter } from '../redux/slices/ordersSlice';
import { toast } from 'react-toastify';
import { seatIdToSectionParentOwn } from '../utils/formatters';
import { downloadFile } from '../utils/fileUtils';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE } from '../utils/paginationUtils';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Download from '@mui/icons-material/Download';
import NoData from '../components/NoData';
import WarningDialog from '../components/WarningDialog';
import TicketPdfSelectorDialog from '../components/TicketPDFSelectorDialog';
import api from '../api';
import { getBtPayErrorMessageKey } from '../utils/btPayErrorMessages';

const VOUCHER_VIEWER_ROLES = new Set(['admin', 'organizer']);

const toDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-');
const parseJsonArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getTicketStatusChip = (usageStatus, t) => {
  const status = usageStatus || 'UNKNOWN';
  const translatedStatus = status;
  const color =
    status === 'VALID'
      ? 'success'
      : status === 'SCANNED'
      ? 'info'
      : status === 'REFUND_PENDING'
      ? 'warning'
      : status === 'CANCELED'
      ? 'error'
      : 'default';

  return <Chip size="small" label={t(translatedStatus)} color={color} variant="outlined" sx={{ borderRadius: 1 }} />;
};

const getOrderStatusChip = (orderStatus, t) => {
  const status = orderStatus || 'UNKNOWN';
  const color =
    status === 'COMPLETED'
      ? 'success'
      : status === 'PAYMENT_FAILURE' || status === 'FAILED'
      ? 'error'
      : status === 'PENDING'
      ? 'warning'
      : 'default';

  return <Chip size="small" label={t(status)} color={color} variant="outlined" sx={{ borderRadius: 1 }} />;
};

function Row({ row }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUpdating = useSelector((state) => state.orders.isUpdating);
  const user = useSelector((state) => state.auth.user);

  const [open, setOpen] = useState(false);
  const [selectedOrdId, setSelectedOrdId] = useState(null);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [cancelEmail, setCancelEmail] = useState(true);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const canExpandByStatus = ['PAYMENT_SUCCESS', 'COMPLETED', 'PAYMENT_FAILURE'].includes(row.status);

  const canExpandOrderDetails = useMemo(() => canExpandByStatus, [canExpandByStatus]);
  const canManageRefundsForOrder = useMemo(
    () => user?.role !== 'organizer' || !!row.is_organizer,
    [user?.role, row.is_organizer]
  );
  const hasValidTicket = useMemo(() => row.tickets.some((ticket) => ticket.status === 'VALID'), [row.tickets]);

  const refundableTickets = useMemo(() => {
    if (!canManageRefundsForOrder) return [];
    if (row.status !== 'COMPLETED') return [];
    return row.tickets.filter((ticket) => ticket.status === 'VALID');
  }, [canManageRefundsForOrder, row.status, row.tickets]);

  useEffect(() => {
    const validIds = new Set(refundableTickets.map((ticket) => ticket.id));
    setSelectedTicketIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [refundableTickets]);

  const allSelected = refundableTickets.length > 0 && selectedTicketIds.length === refundableTickets.length;
  const partiallySelected = selectedTicketIds.length > 0 && !allSelected;

  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
    );
  };

  const toggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTicketIds(refundableTickets.map((ticket) => ticket.id));
      return;
    }
    setSelectedTicketIds([]);
  };

  const handleOpenRefundDialog = () => {
    if (!selectedTicketIds.length) return;
    setOpenRefundDialog(true);
  };

  const handleCloseRefundDialog = () => {
    setOpenRefundDialog(false);
  };

  const handleOnConfirmRefund = () => {
    dispatch(refundTickets({ ticket_ids: selectedTicketIds, sendEmail: cancelEmail })).then((action) => {
      if (!refundTickets.fulfilled.match(action)) return;

      const results = action.payload?.results || [];
      const successCount = results.filter((result) => result.status === 'SUCCESS').length;
      const pendingCount = results.filter((result) => result.status === 'PENDING').length;
      const failedCount = results.filter((result) => result.status === 'FAILED').length;
      const acceptedCount = successCount + pendingCount;

      if (acceptedCount > 0) {
        setSelectedTicketIds([]);
        setOpenRefundDialog(false);
      }

      if (pendingCount > 0 && failedCount === 0 && successCount === 0) {
        toast.success(t('Tickets marked for refund. They will be processed shortly.'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }

      if (successCount > 0 && failedCount === 0 && pendingCount === 0) {
        toast.success(t('Refund completed successfully'), { position: toast.POSITION.BOTTOM_RIGHT });
        return;
      }

      if (acceptedCount > 0 && failedCount > 0) {
        toast.warning(t('Refund partially completed'), { position: toast.POSITION.BOTTOM_RIGHT });
        return;
      }

      toast.error(t('Refund failed'), { position: toast.POSITION.BOTTOM_RIGHT });
    });
  };

  const paymentRcMeaning = row.payment_processor === 'BT' ? t(getBtPayErrorMessageKey(row.payment_rc)) : '-';
  const paymentErrorCode =
    row.payment_rc === null || row.payment_rc === undefined || String(row.payment_rc).trim() === ''
      ? null
      : String(row.payment_rc);
  const showPaymentError = !!paymentErrorCode && paymentErrorCode !== '0' && paymentRcMeaning !== '-';
  const refundActionCode =
    row.last_refund_action_code === null ||
    row.last_refund_action_code === undefined ||
    String(row.last_refund_action_code).trim() === ''
      ? null
      : String(row.last_refund_action_code);
  const refundActionCodeMeaning =
    row.payment_processor === 'BT' && refundActionCode ? t(getBtPayErrorMessageKey(refundActionCode)) : '-';
  const showRefundError = !!refundActionCode && refundActionCode !== '0' && refundActionCodeMeaning !== '-';
  const refundHistory = useMemo(() => parseJsonArray(row.refunds_history), [row.refunds_history]);
  const paymentAmount = Number(row.total);
  const isInvitationOrder = String(row.payment_type || '').toUpperCase() === 'INVITATION';
  const shouldShowPaymentAmount = !isInvitationOrder && Number.isFinite(paymentAmount) && paymentAmount > 0;
  const isAdmin = user?.role === 'admin';
  const isCashier = user?.role === 'cashier';
  const canViewVouchers = VOUCHER_VIEWER_ROLES.has(user?.role);
  const canOpenVoucher = Boolean(canViewVouchers && row.voucher_id && row.voucher_code_snapshot);
  const paymentHistoryRows = useMemo(() => {
    const rows = [];

    if (row.payment_success_at) {
      rows.push({
        timestamp: row.payment_success_at,
        action: 'PAYMENT',
        status: 'SUCCESS',
        errorCode: null,
        amount: isInvitationOrder ? 0 : shouldShowPaymentAmount ? paymentAmount : null,
        errorMessage: null,
      });
    }

    if (row.payment_failure_at) {
      rows.push({
        timestamp: row.payment_failure_at,
        action: 'PAYMENT',
        status: 'FAILED',
        errorCode: paymentErrorCode,
        amount: isInvitationOrder ? 0 : shouldShowPaymentAmount ? paymentAmount : null,
        errorMessage: showPaymentError ? paymentRcMeaning : null,
      });
    }

    refundHistory.forEach((refund) => {
      rows.push({
        timestamp: refund.created_at,
        action: 'REFUND',
        status: refund.status || null,
        errorCode: refund.bt_error_code || refund.bt_action_code || null,
        amount: refund.amount,
        errorMessage: showRefundError ? refundActionCodeMeaning : null,
      });
    });

    return rows.sort((a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf());
  }, [
    isInvitationOrder,
    row.payment_success_at,
    row.payment_failure_at,
    paymentErrorCode,
    paymentAmount,
    paymentRcMeaning,
    refundActionCodeMeaning,
    refundHistory,
    showPaymentError,
    showRefundError,
    shouldShowPaymentAmount,
  ]);

  return (
    <Fragment>
      <TableRow
        sx={{
          '& > td': { whiteSpace: 'nowrap', borderBottom: 'unset' },
          '&:nth-of-type(4n+1)': (theme) => ({
            backgroundColor: theme.palette.action.hover,
          }),
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            disabled={!canExpandOrderDetails}
            size="small"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {hasValidTicket && row.status === 'COMPLETED' ? (
            <Link onClick={() => setSelectedOrdId(row.id)} sx={{ color: 'white', cursor: 'pointer' }} underline="hover">
              {row.id}
            </Link>
          ) : (
            row.id
          )}
        </TableCell>
        <TableCell>{row.order_reference}</TableCell>
        <TableCell>{row.payment_rrn || '-'}</TableCell>
        <TableCell>{dayjs(row.created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
        <TableCell>{dayjs(row.status_updated_at).format('DD/MM/YYYY HH:mm')}</TableCell>
        <TableCell>{row.total}</TableCell>
        {isCashier && <TableCell align="right">{Number(row.voucher_discount_total || 0).toFixed(2)}</TableCell>}
        {canViewVouchers && (
          <TableCell>
            {canOpenVoucher ? (
              <Link component={RouterLink} to={`/vouchers/${row.voucher_id}`} underline="hover">
                {row.voucher_code_snapshot}
              </Link>
            ) : (
              row.voucher_code_snapshot || '-'
            )}
          </TableCell>
        )}
        <TableCell>
          {row.first_name || row.last_name ? `${row.first_name || ''} ${row.last_name || ''}`.trim() : '-'}
        </TableCell>
        <TableCell>{row.email || '-'}</TableCell>
        <TableCell>{row.phone || '-'}</TableCell>
        <TableCell>{row.is_cashier ? 'cashier' : row.is_organizer ? 'organizer' : 'client'}</TableCell>
        {isAdmin && <TableCell>{getOrderStatusChip(row.status, t)}</TableCell>}
      </TableRow>

      <TableRow sx={{ '&:last-child > td, &:last-child > th': { border: 0 } }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={20}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 8, mr: 8, mt: 3, mb: 4 }}>
              {isAdmin && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {t('Payment History')}
                  </Typography>
                  <Table size="small" sx={{ mb: 3 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('Timestamp')}</TableCell>
                        <TableCell>{t('Action')}</TableCell>
                        <TableCell>{t('StatusLabel')}</TableCell>
                        <TableCell>{t('Amount (Lei)')}</TableCell>
                        <TableCell>{t('Error code')}</TableCell>
                        <TableCell>{t('Payment error message')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistoryRows.map((item, index) => {
                        return (
                          <TableRow key={`${item.action}_${item.timestamp || 'na'}_${index}`}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{toDate(item.timestamp)}</TableCell>
                            <TableCell>{item.action === 'PAYMENT' ? t('Payment') : t('Refund')}</TableCell>
                            <TableCell>{item.status ? t(item.status) : '-'}</TableCell>
                            <TableCell>
                              {item.amount !== undefined && item.amount !== null
                                ? `${item.amount}`
                                : item.action === 'PAYMENT' && isInvitationOrder
                                ? '0'
                                : '-'}
                            </TableCell>
                            <TableCell>{item.errorCode || '-'}</TableCell>
                            <TableCell>{item.errorMessage || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!paymentHistoryRows.length && (
                        <TableRow>
                          <TableCell colSpan={6}>-</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}

              {!!row.tickets.length && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                    <Typography variant="h6">{t('Tickets')}</Typography>
                    {canManageRefundsForOrder && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!selectedTicketIds.length}
                        onClick={handleOpenRefundDialog}
                      >
                        {t('Cancel')}
                      </Button>
                    )}
                  </Box>

                  <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {canManageRefundsForOrder && (
                            <Checkbox
                              size="small"
                              checked={allSelected}
                              indeterminate={partiallySelected}
                              onChange={toggleSelectAll}
                              disabled={!refundableTickets.length}
                            />
                          )}
                        </TableCell>
                        <TableCell>{t('Organizer')}</TableCell>
                        <TableCell>{t('Event')}</TableCell>
                        <TableCell>{t('Price (Lei)')}</TableCell>
                        <TableCell>{t('Category')}</TableCell>
                        <TableCell>{t('Serial')}</TableCell>
                        <TableCell>{t('Fiscal Serial')}</TableCell>
                        <TableCell>{t('StatusLabel')}</TableCell>
                        <TableCell>{t('Seat ID')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.tickets.map((ticket) => {
                        const ticketCanRefund =
                          canManageRefundsForOrder && ticket.status === 'VALID' && row.status === 'COMPLETED';
                        return (
                          <TableRow key={ticket.id}>
                            <TableCell>
                              {canManageRefundsForOrder && (
                                <Checkbox
                                  size="small"
                                  checked={selectedTicketIds.includes(ticket.id)}
                                  onChange={() => toggleTicketSelection(ticket.id)}
                                  disabled={!ticketCanRefund}
                                />
                              )}
                            </TableCell>
                            <TableCell>{ticket.organizer}</TableCell>
                            <TableCell>
                              <Link
                                underline="hover"
                                href={`events/${ticket.events_id}`}
                                sx={{
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'inline-block',
                                  maxWidth: 440,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {`(${ticket.events_id}) ${ticket.title} - ${ticket.city}`}
                              </Link>
                            </TableCell>
                            <TableCell>{ticket.price}</TableCell>
                            <TableCell>
                              {ticket.category_name}
                              {!ticket.is_default_type && (
                                <Chip sx={{ ml: 1 }} size="small" label={ticket.type_label} />
                              )}
                            </TableCell>
                            <TableCell>{ticket.serial}</TableCell>
                            <TableCell>{ticket.fiscal_serial || '-'}</TableCell>
                            <TableCell>{getTicketStatusChip(ticket.status, t)}</TableCell>
                            <TableCell>
                              {seatIdToSectionParentOwn(
                                ticket.seatsio_object_id,
                                ticket.section,
                                ticket.parent,
                                ticket.own,
                                t
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <TicketPdfSelectorDialog orderId={selectedOrdId} onClose={() => setSelectedOrdId(null)} />
      <WarningDialog
        title={t('Confirm refund')}
        content={t('By pressing confirm you will refund selected tickets and release their seats.')}
        loading={isUpdating}
        open={openRefundDialog}
        onCancel={handleCloseRefundDialog}
        onConfirm={handleOnConfirmRefund}
        checkboxLabel={t('Send cancel order email')}
        checkboxChecked={cancelEmail}
        onCheckboxChange={(e) => setCancelEmail(e.target.checked)}
      />
    </Fragment>
  );
}

function OrdersTable({ orders, onExportCsv, pagination }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const { totalItems, page, limit, onPageChange, onRowsPerPageChange } = pagination;
  const canViewVouchers = VOUCHER_VIEWER_ROLES.has(user?.role);
  const isCashier = user?.role === 'cashier';

  return orders.length ? (
    <>
      <Grid container alignItems="center" justifyContent="end">
        <IconButton onClick={onExportCsv} sx={{ mb: 1 }} color="primary" size="medium">
          <Download />
        </IconButton>
      </Grid>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& > th': { whiteSpace: 'nowrap', borderBottom: 'unset' } }}>
                <TableCell sx={{ width: 50 }} />
                <TableCell>{t('Order ID')}</TableCell>
                <TableCell>{t('Order Ref')}</TableCell>
                <TableCell>{t('Payment Ref')}</TableCell>
                <TableCell>{t('Created')}</TableCell>
                <TableCell>{t('Updated')}</TableCell>
                <TableCell>{t('Total (Lei)')}</TableCell>
                {isCashier && <TableCell align="right">{t('Voucher (Lei)')}</TableCell>}
                {canViewVouchers && <TableCell>{t('Vouchers')}</TableCell>}
                <TableCell>{t('Name')}</TableCell>
                <TableCell>{t('Email')}</TableCell>
                <TableCell>{t('Phone')}</TableCell>
                <TableCell>{t('Originator')}</TableCell>
                {user?.role === 'admin' && <TableCell>{t('StatusLabel')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <Row key={order.id} row={order} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={onPageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE}
          labelRowsPerPage={t('rowsPaginator')}
        />
      </Paper>
    </>
  ) : (
    <NoData />
  );
}

export default function Orders() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const orders = useSelector((state) => state.orders.data);
  const ordersError = useSelector((state) => state.orders.error);
  const isLoading = useSelector((state) => state.orders.isLoading);
  const user = useSelector((state) => state.auth.user);

  const totalItems = useSelector((state) => state.orders.totalItems);
  const options = useSelector((state) => state.orders.options);

  const from = useMemo(() => (options.from ? dayjs(options.from) : null), [options.from]);
  const to = useMemo(() => (options.to ? dayjs(options.to) : null), [options.to]);

  useEffect(() => {
    return () => {
      dispatch(clearOrders());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearOrders());
    const filtersFromUrl = {
      page: parseInt(searchParams.get('page')) || DEFAULT_PAGE,
      limit: parseInt(searchParams.get('limit')) || DEFAULT_ROWS_PER_PAGE,
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || 'ALL',
      from: searchParams.get('from'),
      to: searchParams.get('to'),
    };

    dispatch(setOrdersFilter(filtersFromUrl));
    dispatch(fetchOrders(filtersFromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchParams.get('page'), searchParams.get('limit')]);

  useEffect(() => {
    setSearchParams(
      {
        page: options.page,
        limit: options.limit,
        search: options.search || '',
        status: options.status || 'ALL',
        from: options.from,
        to: options.to,
      },
      { replace: true }
    );
  }, [options.page, options.limit, options.search, options.status, setSearchParams, options.from, options.to]);

  const updateFilters = (patch) => {
    const updated = { ...options, ...patch };
    setSearchParams(updated);
  };

  const handleOnSearchClick = () => updateFilters({ page: 0 });
  const handleChangePage = (_, newPage) => updateFilters({ page: newPage + 1 });
  const handleChangeRowsPerPage = (e) => updateFilters({ limit: +e.target.value, page: 1 });

  const downloadOrdersCsv = () => {
    api
      .get(
        `/orders/csv?to=${encodeURIComponent(options.to)}&from=${encodeURIComponent(
          options.from
        )}&status=${encodeURIComponent(options.status || 'ALL')}&search=${encodeURIComponent(options.search)}`,
        { responseType: 'blob' }
      )
      .then((res) => {
        const data = res.data;
        downloadFile(
          data,
          'text/csv',
          `orders_${dayjs().unix()}_${dayjs(options.from).unix()}_${dayjs(options.to).unix()}.csv`
        );
      })
      .catch(() => {
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  useEffect(() => {
    if (ordersError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [ordersError, t]);

  const searchHelperText = t('Search using order id, event id, name, email, phone or RRN');

  return (
    <div className="orders">
      <Typography variant="h3">{t('Orders')}</Typography>
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                onChange={(event) => dispatch(setOrdersFilter({ search: event.target.value }))}
                value={options.search}
                sx={{ marginRight: 4 }}
                inputProps={{ autoComplete: 'off' }}
                label={t('Search')}
                fullWidth
                placeholder={searchHelperText}
                variant="outlined"
                type="text"
              />
            </Grid>
            <Grid item xs={12} md>
              <DatePicker
                disableFuture
                maxDate={to}
                onChange={(value) =>
                  dispatch(
                    setOrdersFilter({
                      from: value ? dayjs(value).startOf('day').toISOString() : null,
                    })
                  )
                }
                value={from}
                sx={{ marginRight: 3 }}
                slotProps={{ textField: { label: t('From'), fullWidth: true, variant: 'outlined', error: false } }}
              />
            </Grid>
            <Grid item xs={12} md>
              <DatePicker
                disableFuture
                minDate={from}
                onChange={(value) =>
                  dispatch(
                    setOrdersFilter({
                      to: value ? dayjs(value).endOf('day').toISOString() : null,
                    })
                  )
                }
                value={to}
                sx={{ marginRight: 3 }}
                slotProps={{ textField: { label: t('To'), fullWidth: true, variant: 'outlined', error: false } }}
              />
            </Grid>
            {user?.role === 'admin' && (
              <Grid item xs={12} md>
                <FormControl fullWidth>
                  <InputLabel id="orders-status-filter-label">{t('StatusLabel')}</InputLabel>
                  <Select
                    labelId="orders-status-filter-label"
                    label={t('StatusLabel')}
                    value={options.status || 'ALL'}
                    onChange={(event) => dispatch(setOrdersFilter({ status: event.target.value }))}
                  >
                    <MenuItem value="ALL">{t('ALL')}</MenuItem>
                    <MenuItem value="COMPLETED">{t('COMPLETED')}</MenuItem>
                    <MenuItem value="PAYMENT_FAILURE">{t('PAYMENT_FAILURE')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md>
              <Button disabled={isLoading} fullWidth onClick={handleOnSearchClick} size="large" variant="contained">
                {t('Search')}
                {isLoading && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <OrdersTable
          orders={orders}
          onExportCsv={downloadOrdersCsv}
          pagination={{
            totalItems,
            page: options.page,
            limit: options.limit,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </Box>
    </div>
  );
}
